<template>
    <div
        class="flex mt-[42px] justify-between h-[42px]"
        :class="hasReportsAccess ? 'mb-[27px]' : ' mb-[42px]'"
    >
        <h1
            class="text-[21px] font-medium leading-normal tracking-small text-gray-800"
        >
            {{ $t("vue.reports_header") }}
        </h1>
        <div>
            <div
                :class="[
                    'relative',
                    shouldShowDateRangeUpgradeOverlay &&
                        'cursor-pointer opacity-50',
                ]"
                @click="
                    shouldShowDateRangeUpgradeOverlay &&
                    checkAccessOrShowModal()
                "
            >
                <DateRangePicker
                    v-model="selectedDateRange"
                    :display-full-block="true"
                    @update:model-value="handleDateRangeChange"
                />
                <div
                    v-if="shouldShowDateRangeUpgradeOverlay"
                    class="absolute inset-0 bg-transparent cursor-pointer"
                    title="Click for upgrade options - Scale plan required"
                />
            </div>
        </div>
    </div>

    <div v-if="showPastDueWarning">
        <PastDueWarningBlockWhite class="mt-[42px] mb-[42px]" />
    </div>

    <div v-else-if="!hasReportsAccess">
        <InfoCard
            :img-src="trialEndedImg"
            img-alt="Trial ended"
            :title="t('vue.your_trial_ended')"
            :body-text="t('vue.your_trial_ended_action_step')"
            :emphasis-text="t('vue.your_trial_ended_action_step_continue')"
            :button-label="t('vue.choose_dmarceye_plan')"
            :button-href="route('billing.subscriptions.index')"
        />
    </div>

    <div v-else>
        <div>
            <BasicTabs :tabs="tabs" :tab-key="'domain-overview'" />
        </div>

        <FreePlanReportsLimitBlock />

        <!-- Upgrade Required Section -->
        <!--TODO: will be resolved later when feature accesses will be reconsidered-->
        <!--        <div v-if="!hasReportsAccess">-->
        <!--            <InfoCard-->
        <!--                :img-src="lockedNotAuthorized"-->
        <!--                img-alt="User has no access to content"-->
        <!--                :title="t('vue.upgrade_required')"-->
        <!--                :body-text="t('vue.not_having_access_message')"-->
        <!--                :show-dot="false"-->
        <!--                :button-label="t('vue.upgrade_my_plan')"-->
        <!--                :button-href="route('billing.subscriptions.index')"-->
        <!--                :disable-top-margin="false"-->
        <!--            />-->
        <!--        </div>-->

        <!-- Main Content -->
        <div v-if="hasReportsAccess">
            <div class="mt-[42px]">
                <StatsBar
                    :stats="{ pass: totalPass, fail: totalFail }"
                    :stats-before="{
                        pass: totalPassBefore,
                        fail: totalFailBefore,
                    }"
                    :statistics="statistics"
                />
            </div>
            <!--// if is free plan, dont show-->

            <div
                v-if="!page.props.hasFreePlan && statistics.totalDomains === 0"
                class="mt-6"
            >
                <InfoCard
                    :img-src="searchSvg"
                    img-alt="Reports none image"
                    :title="t('vue.waiting_for_reports')"
                    :body-text="t('vue.no_results_for_domain')"
                    :show-dot="false"
                    :disable-top-margin="true"
                >
                    <template #actions>
                        <AtomicButton
                            v-if="!page.props.demoMode"
                            color="yellow"
                            size="md"
                            class="h-8 px-4 text-[12px] font-semibold leading-4"
                            @click="goToDomainWizardBlank"
                        >
                            {{ t("vue.add_new_domain") }}
                        </AtomicButton>
                    </template>
                </InfoCard>
            </div>

            <div v-else>
                <div class="mb-[22px] grid grid-cols-2 gap-5 rounded-[12.8px]">
                    <ReportBox>
                        <p class="font-medium text-gray-900">
                            {{ t("vue.quick_insights") }}
                        </p>

                        <div class="mt-4 grid gap-3">
                            <InsightBox
                                v-for="insight in insights"
                                :key="insight.title"
                                :variant="insight.variant"
                            >
                                <template #title>{{ insight.title }}</template>
                                <template #content>
                                    <span v-html="insight.content"></span>
                                </template>
                            </InsightBox>
                        </div>
                    </ReportBox>

                    <ReportBox>
                        <p class="font-medium text-gray-900">
                            {{ t("vue.ai_recommendations") }}
                        </p>

                        <div
                            v-if="aiAssessment.status === 'processing'"
                            class="mt-4"
                        >
                            <div class="flex items-center gap-3">
                                <div
                                    class="h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-r-transparent"
                                ></div>
                                <span class="text-sm text-gray-600"
                                    >Generating AI insights...</span
                                >
                            </div>
                        </div>

                        <div
                            v-else-if="aiAssessment.recommendations"
                            class="mt-4"
                        >
                            <div
                                class="rounded-lg border border-yellow-100 bg-yellow-50 p-4"
                            >
                                <div class="flex items-start gap-4">
                                    <div
                                        class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-yellow-100"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            class="lucide lucide-brain h-5 w-5 text-yellow-600"
                                            aria-hidden="true"
                                        >
                                            <path
                                                d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z"
                                            ></path>
                                            <path
                                                d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z"
                                            ></path>
                                            <path
                                                d="M15 13a4.5 4.5 0 0 1-3-4 4.5 4.5 0 0 1-3 4"
                                            ></path>
                                            <path
                                                d="M17.599 6.5a3 3 0 0 0 .399-1.375"
                                            ></path>
                                            <path
                                                d="M6.003 5.125A3 3 0 0 0 6.401 6.5"
                                            ></path>
                                            <path
                                                d="M3.477 10.896a4 4 0 0 1 .585-.396"
                                            ></path>
                                            <path
                                                d="M19.938 10.5a4 4 0 0 1 .585.396"
                                            ></path>
                                            <path
                                                d="M6 18a4 4 0 0 1-1.967-.516"
                                            ></path>
                                            <path
                                                d="M19.967 17.484A4 4 0 0 1 18 18"
                                            ></path>
                                        </svg>
                                    </div>
                                    <div class="flex-1">
                                        <h5
                                            class="mb-2 mt-1 font-medium text-yellow-900"
                                        >
                                            {{ $t("vue.ai_recommendations") }}
                                        </h5>
                                        <AiAssessmentText
                                            class="text-md mb-3 text-sm leading-8 text-yellow-800"
                                            :content="
                                                aiAssessment.recommendations
                                            "
                                            :format="
                                                aiAssessment.field_formats
                                                    ?.recommendations
                                            "
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div v-else class="mt-4">
                            <div class="flex items-center gap-3 text-gray-500">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    class="lucide lucide-brain h-5 w-5"
                                >
                                    <path
                                        d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z"
                                    ></path>
                                    <path
                                        d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z"
                                    ></path>
                                    <path
                                        d="M15 13a4.5 4.5 0 0 1-3-4 4.5 4.5 0 0 1-3 4"
                                    ></path>
                                    <path
                                        d="M17.599 6.5a3 3 0 0 0 .399-1.375"
                                    ></path>
                                    <path
                                        d="M6.003 5.125A3 3 0 0 0 6.401 6.5"
                                    ></path>
                                    <path
                                        d="M3.477 10.896a4 4 0 0 1 .585-.396"
                                    ></path>
                                    <path
                                        d="M19.938 10.5a4 4 0 0 1 .585.396"
                                    ></path>
                                    <path d="M6 18a4 4 0 0 1-1.967-.516"></path>
                                    <path
                                        d="M19.967 17.484A4 4 0 0 1 18 18"
                                    ></path>
                                </svg>
                                <span class="text-sm">{{
                                    $t(
                                        "vue.ai_recommendations_will_appear_here_when_available",
                                    )
                                }}</span>
                            </div>
                        </div>
                    </ReportBox>
                </div>

                <div class="mt-5">
                    <StatsChart
                        v-if="statistics.totalEmailsReported > 0"
                        :chart-data="chartData"
                        :header="$t('vue.daily_email_reports')"
                    ></StatsChart>
                </div>

                <!--                <div-->
                <!--                    v-if="geoItems.some((item) => item.geo !== null)"-->
                <!--                    class="mb-[22px] rounded-[12.8px] border border-gray-200 bg-white p-6"-->
                <!--                >-->
                <!--                    <h3 class="mb-1 text-sm font-semibold text-gray-900">-->
                <!--                        {{ $t("vue.ip_geo_map_header") }}-->
                <!--                    </h3>-->
                <!--                    <p class="mb-4 text-sm text-gray-500">-->
                <!--                        {{ $t("vue.ip_geo_map_description") }}-->
                <!--                    </p>-->
                <!--                    <IpGeoMap :items="geoItems" />-->
                <!--                </div>-->

                <div class="mb-24">
                    <DomainsTable
                        :active-domains="activeDomains"
                        :date-range="dateRange"
                        :table="table"
                        :pagination="pagination"
                    />
                </div>
            </div>
        </div>
    </div>

    <UpgradeRequiredModal
        :opened="showUpgradeModal"
        title="Scale Plan Required"
        description="Custom date range selection requires a Scale plan subscription. Upgrade your subscription to access advanced filtering features."
        @close="closeUpgradeModal"
    />

    <CreateDomainModal
        v-if="createDomainModal"
        @close="closeCreateDomainModal"
    />

    <DomainLimitModal
        :show="showDomainLimitModal"
        :message="domainLimitMessage"
        @close="closeDomainLimitModal"
    />

    <TrialEndedPopup />
    <!--    <ExtendYourTrialPopup />-->
    <!--    <FreePlanPopup />-->
    <FreePlanMaxReachedPopup />
</template>

<script lang="ts" setup>
import BasicTabs from "@/components/Atomic/Tabs/BasicTabs.vue";
import UpgradeRequiredModal from "@/components/Atomic/Modals/UpgradeRequiredModal.vue";
import useReportsAccess from "@/composables/useReportsAccess";
import useDateRangeAccess from "@/composables/useDateRangeAccess";
import useNumberFormat from "@/composables/useNumberFormat";
import AiAssessmentText from "@/components/Content/AiAssessmentText.vue";
import InsightBox from "./components/InsightBox.vue";
import DateRangePicker from "./components/DateRangePicker.vue";
import ReportBox from "./components/ReportBox.vue";
import DonutChart from "./components/DonutChart/DonutChart.vue";
import StatsChart from "./components/StatsChart.vue";
import DomainsTable from "./components/DomainsTable.vue";
import IpGeoMap from "@/components/Special/IpGeoMap.vue";
import {
    Globe,
    AlertOctagon,
    Settings,
    Mail,
    Shield,
    ShieldAlertIcon,
    BellIcon,
    Bell,
} from "lucide-vue-next";
import { ref, computed, onMounted } from "vue";
import { getLocalTimeZone, today, parseDate } from "@internationalized/date";
import type { DateRange } from "reka-ui";
import { defineProps } from "vue";
import { router, usePage } from "@inertiajs/vue3";
import route from "ziggy-js";
import axios from "axios";

import type TabItem from "@/types/Settings/TabItem";
import Notifications from "@/components/Notifications.vue";
import DateRangeField from "@/Pages/Reports/components/DateRangeField.vue";
import StatsBar from "@/Pages/Reports/components/StatsBar.vue";
import { Chart } from "chart.js";
import { ChartBarIcon } from "@heroicons/vue/24/outline";
import { BellAlertIcon } from "@heroicons/vue/20/solid";
import { useTranslations } from "@/composables/useTranslations.ts";
import CreateDomainModal from "@/Pages/Domains/components/CreateDomainModal.vue";
import DomainLimitModal from "@/Pages/Domains/components/DomainLimitModal.vue";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps.ts";
import InfoCard from "@/components/Content/InfoCard.vue";
import FreePlanMaxReachedPopup from "@/components/Atomic/Dialogs/Popups/FreePlanMaxReachedPopup.vue";
import FreePlanReportsLimitBlock from "@/Pages/Reports/components/FreePlanReportsLimitBlock.vue";
import PastDueWarningBlockWhite from "@/components/Atomic/Miscellaneous/PastDueWarningBlockWhite.vue";
import usePastDueWarning from "@/composables/usePastDueWarning.ts";

const { t } = useTranslations();
const { hasReportsAccess } = useReportsAccess();
const page = usePage<IRhizomPageProps>();

const searchSvg = new URL(
    "/resources/js/assets/images/search.svg",
    import.meta.url,
).href;

const trialEndedImg = new URL(
    "/resources/js/assets/images/plan-calendar.svg",
    import.meta.url,
).href;

const lockedNotAuthorized = new URL(
    "/resources/js/assets/images/locked-not-authorized.svg",
    import.meta.url,
).href;
const {
    hasDateRangeAccess,
    showUpgradeModal,
    checkAccessOrShowModal,
    closeUpgradeModal,
} = useDateRangeAccess();
const { formatNumber } = useNumberFormat();

const props = defineProps<{
    statistics: {
        activeDomains: number;
        totalEmailsReported: number;
        dmarcCompliant: number;
        dmarcNonCompliant: number;
        complianceRate: number;
        totalDomains: number;
    };
    activeDomains: Array<{
        domain: string;
        active: boolean;
        parked?: boolean | null;
        dmarcPolicy: string;
        report_current: { pass: number; fail: number; sum: number };
    }>;
    chartData: Array<{ day: string; pass: number; fail: number }>;
    dateRange: { start: string; end: string };
    insights: Array<{ variant: string; title: string; content: string }>;
    geoItems: Array<{
        ip: string;
        label: string;
        geo: {
            latitude: number;
            longitude: number;
            city: string | null;
            region: string | null;
            country: string | null;
            org: string | null;
            timezone: string | null;
        } | null;
    }>;

    table: {
        q: string;
        sort_by: "domain" | "emails" | "compliance" | "policy";
        sort_dir: "asc" | "desc";
        per_page: number;
        current_page: number;
    };
    pagination: {
        total: number;
        per_page: number;
        current_page: number;
        last_page: number;
        from: number;
        to: number;
    };
}>();

const donutData = computed(() => [
    { key: "compliant", value: props.statistics.complianceRate },
    { key: "non-compliant", value: 100 - props.statistics.complianceRate },
]);

const aiAssessment = ref<any>({});

const shouldShowDateRangeUpgradeOverlay = computed(() => {
    return !hasDateRangeAccess.value;
});

const selectedDateRange = ref<DateRange>({
    start: parseDate(props.dateRange.start),
    end: parseDate(props.dateRange.end),
});

const handleDateRangeChange = (newRange: DateRange) => {
    if (!checkAccessOrShowModal()) return;
    if (!newRange.start || !newRange.end) return;

    const startDate = newRange.start.toString();
    const endDate = newRange.end.toString();

    router.get(
        route("reports.index"),
        {
            start_date: startDate,
            end_date: endDate,
            q: props.table?.q || undefined,
            sort_by: props.table?.sort_by ?? "emails",
            sort_dir: props.table?.sort_dir ?? "desc",
            per_page: props.table?.per_page ?? 10,
            current_page: 1,
        },
        { preserveState: false, preserveScroll: true },
    );
};

const fetchAiAssessment = async () => {
    try {
        // const res = await axios.get(route("reports.ai"));
        // aiAssessment.value = res.data;
        // if (res.data.status === "processing") {
        //     setTimeout(fetchAiAssessment, 5000);
        // }
    } catch (e) {
        console.error(e);
    }
};

onMounted(fetchAiAssessment);

const tabs: TabItem[] = (() => {
    const links: TabItem[] = [
        {
            title: t("vue.overview"),
            key: "domain-overview",
            icon: ChartBarIcon,
            url: "/reports",
        },
        {
            title: t("vue.notifications"),
            key: "notifications",
            icon: Bell,
            url: "/reports/notifications",
        },
    ];

    if (page.props.hasFreePlan) {
        links.splice(1, 1);
    }

    return links;
})();

const canAddDomain = computed(() => {
    const hasFreePlan = page.props.hasFreePlan;
    const hasScalePlan = page.props.hasScalePlan;
    const domainCount = page.props.totalDomainsCount;
    const hasGodMode = page.props.hasGodMode;

    if (hasGodMode || hasFreePlan) {
        return true;
    }

    if (!hasScalePlan) {
        return domainCount < 1;
    }

    if (hasScalePlan) {
        return domainCount < 50;
    }

    return page.props.isTrialing;
});

const createDomainModal = ref(false);
const showDomainLimitModal = ref(false);
const domainLimitMessage = ref("");

const closeCreateDomainModal = () => {
    createDomainModal.value = false;

    router.visit(route("reports.index"), {
        method: "get",
        preserveState: false,
        preserveScroll: false,
    });
};

const { showPastDueWarning } = usePastDueWarning();

const closeDomainLimitModal = () => {
    showDomainLimitModal.value = false;
    domainLimitMessage.value = "";
};

const goToDomainWizardBlank = () => {
    router.get(route("domainGuide.index"));
};
</script>
